/**
 * FX Rate Service for frontend currency conversion
 * Integrates with ExchangeRate.host API as specified in currency conversion requirements
 */

const FX_RATE_SERVICE = {
  BASE_URL: 'https://api.frankfurter.dev/v1',
  CACHE_DURATION: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
  REQUEST_TIMEOUT: 10000, // 10 seconds
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000, // 1 second
  
  // Debounce map to prevent rapid API calls
  debounceMap: new Map(),
  
  /**
   * Generate cache key for FX rate
   */
  getCacheKey(date, fromCurrency, toCurrency) {
    return `fx_rate_${date}_${fromCurrency}_${toCurrency}`;
  },
  
  /**
   * Get cached FX rate from localStorage
   */
  getCachedRate(date, fromCurrency, toCurrency) {
    try {
      const cacheKey = this.getCacheKey(date, fromCurrency, toCurrency);
      const cached = localStorage.getItem(cacheKey);
      
      if (cached) {
        const data = JSON.parse(cached);
        const now = Date.now();
        
        // Check if cache is still valid
        if (now - data.timestamp < this.CACHE_DURATION) {
          console.log(`Retrieved cached FX rate for ${fromCurrency} -> ${toCurrency} on ${date}: ${data.rate}`);
          return {
            rate: parseFloat(data.rate),
            date: date,
            success: true,
            error: null,
            cached: true
          };
        } else {
          // Remove expired cache
          localStorage.removeItem(cacheKey);
        }
      }
    } catch (error) {
      console.error('Error reading FX rate cache:', error);
    }
    
    return null;
  },
  
  /**
   * Cache FX rate in localStorage
   */
  setCachedRate(date, fromCurrency, toCurrency, rate) {
    try {
      const cacheKey = this.getCacheKey(date, fromCurrency, toCurrency);
      const data = {
        rate: rate,
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(data));
      console.log(`Cached FX rate: ${fromCurrency} -> ${toCurrency} on ${date} = ${rate}`);
    } catch (error) {
      console.error('Error caching FX rate:', error);
    }
  },
  
  /**
   * Fetch FX rate from backend proxy (fallback for CORS issues)
   */
  async fetchFromBackendProxy(date, fromCurrency, toCurrency, places = 6) {
    try {
      console.log(`Fetching FX rate from backend proxy: ${fromCurrency} -> ${toCurrency} on ${date}`);
      
      const params = new URLSearchParams({
        date: date,
        from_currency: fromCurrency,
        to_currency: toCurrency,
        places: places.toString()
      });
      
      const response = await fetch(`/api/deliveries/fx-rate/?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.rate) {
        // Cache the successful result
        this.setCachedRate(date, fromCurrency, toCurrency, data.rate);
        
        console.log(`Successfully fetched FX rate from backend: ${fromCurrency} -> ${toCurrency} on ${date} = ${data.rate}`);
        
        return {
          rate: data.rate,
          date: date,
          success: true,
          error: null,
          cached: false
        };
      } else {
        return {
          rate: null,
          date: date,
          success: false,
          error: data.error || 'Backend proxy failed',
          cached: false
        };
      }
      
    } catch (error) {
      console.error('Backend proxy error:', error);
      return {
        rate: null,
        date: date,
        success: false,
        error: `Backend proxy error: ${error.message}`,
        cached: false
      };
    }
  },
  
  /**
   * Fetch FX rate from API with retry logic
   */
  async fetchFromAPI(date, fromCurrency, toCurrency, places = 6) {
    for (let attempt = 1; attempt <= this.MAX_RETRIES; attempt++) {
      try {
        console.log(`Fetching FX rate from API (attempt ${attempt}/${this.MAX_RETRIES}): ${fromCurrency} -> ${toCurrency} on ${date}`);
        
        // Determine if we need historical or latest rate
        const today = new Date().toISOString().split('T')[0];
        const url = date === today 
          ? `${this.BASE_URL}/latest`
          : `${this.BASE_URL}/${date}`;
        
        const params = new URLSearchParams({
          base: fromCurrency,
          symbols: toCurrency
        });
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.REQUEST_TIMEOUT);
        
        const response = await fetch(`${url}?${params}`, {
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Frankfurter API doesn't have a 'success' field, so we check for the presence of 'rates'
        if (!data.rates) {
          const errorMsg = data.message || 'No rates data returned';
          console.error(`API returned error: ${errorMsg}`);
          
          if (attempt === this.MAX_RETRIES) {
            return {
              rate: null,
              date: date,
              success: false,
              error: `API error: ${errorMsg}`,
              cached: false
            };
          }
          continue;
        }
        
        // Extract the rate
        const rates = data.rates || {};
        if (!(toCurrency in rates)) {
          const errorMsg = `Rate not found for ${fromCurrency} -> ${toCurrency}`;
          console.error(errorMsg);
          
          if (attempt === this.MAX_RETRIES) {
            return {
              rate: null,
              date: date,
              success: false,
              error: errorMsg,
              cached: false
            };
          }
          continue;
        }
        
        const rateValue = rates[toCurrency];
        
        // Validate rate value
        if (typeof rateValue !== 'number' || rateValue <= 0) {
          const errorMsg = `Invalid rate value: ${rateValue}`;
          console.error(errorMsg);
          
          if (attempt === this.MAX_RETRIES) {
            return {
              rate: null,
              date: date,
              success: false,
              error: errorMsg,
              cached: false
            };
          }
          continue;
        }
        
        // Cache the successful result
        this.setCachedRate(date, fromCurrency, toCurrency, rateValue);
        
        console.log(`Successfully fetched FX rate: ${fromCurrency} -> ${toCurrency} on ${date} = ${rateValue}`);
        
        return {
          rate: rateValue,
          date: date,
          success: true,
          error: null,
          cached: false
        };
        
      } catch (error) {
        console.error(`Network error fetching FX rate (attempt ${attempt}):`, error);
        
        // Check if this is a CORS error
        if (error.message.includes('Load failed') || error.message.includes('CORS') || error.message.includes('Access-Control')) {
          console.warn('CORS error detected - this is expected when testing locally. In production, consider using a backend proxy for FX rates.');
        }
        
        if (attempt < this.MAX_RETRIES) {
          // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY * attempt));
          continue;
        }
        
        // If all direct API attempts failed, try backend proxy as fallback
        console.log('Direct API calls failed, trying backend proxy...');
        return await this.fetchFromBackendProxy(date, fromCurrency, toCurrency, places);
      }
    }
  },
  
  /**
   * Get FX rate with caching and debouncing
   */
  async getFXRate(date, fromCurrency, toCurrency, places = 6) {
    // Validate inputs
    if (!date || !fromCurrency || !toCurrency) {
      console.error('Missing required parameters for FX rate lookup');
      return {
        rate: null,
        date: date,
        success: false,
        error: 'Missing required parameters',
        cached: false
      };
    }
    
    // If currencies are the same, return rate of 1
    if (fromCurrency === toCurrency) {
      console.log(`Same currency conversion ${fromCurrency} -> ${toCurrency}, returning rate 1.0`);
      return {
        rate: 1.0,
        date: date,
        success: true,
        error: null,
        cached: false
      };
    }
    
    // Check cache first
    const cachedResult = this.getCachedRate(date, fromCurrency, toCurrency);
    if (cachedResult) {
      return cachedResult;
    }
    
    // Check if there's already a pending request for this rate
    const requestKey = `${date}_${fromCurrency}_${toCurrency}`;
    if (this.debounceMap.has(requestKey)) {
      console.log(`Waiting for existing FX rate request: ${requestKey}`);
      return await this.debounceMap.get(requestKey);
    }
    
    // Create new request promise
    const requestPromise = this.fetchFromAPI(date, fromCurrency, toCurrency, places);
    this.debounceMap.set(requestKey, requestPromise);
    
    try {
      const result = await requestPromise;
      return result;
    } finally {
      // Clean up debounce map
      this.debounceMap.delete(requestKey);
    }
  },
  
  /**
   * Convert amount using FX rate
   */
  async convertAmount(amount, date, fromCurrency, toCurrency) {
    try {
      const amountNum = parseFloat(amount);
      if (isNaN(amountNum)) {
        console.error(`Invalid amount for conversion: ${amount}`);
        return {
          convertedAmount: null,
          originalAmount: amount,
          fxRate: null,
          success: false,
          error: 'Invalid amount'
        };
      }
      
      // Get FX rate
      const rateResult = await this.getFXRate(date, fromCurrency, toCurrency);
      
      if (!rateResult.success) {
        return {
          convertedAmount: null,
          originalAmount: amountNum,
          fxRate: null,
          success: false,
          error: rateResult.error
        };
      }
      
      // Calculate converted amount
      const fxRate = rateResult.rate;
      const convertedAmount = amountNum * fxRate;
      
      console.log(`Converted ${amountNum} ${fromCurrency} to ${convertedAmount} ${toCurrency} using rate ${fxRate}`);
      
      return {
        convertedAmount: convertedAmount,
        originalAmount: amountNum,
        fxRate: fxRate,
        success: true,
        error: null
      };
      
    } catch (error) {
      console.error('Error in amount conversion:', error);
      return {
        convertedAmount: null,
        originalAmount: amount,
        fxRate: null,
        success: false,
        error: error.message
      };
    }
  },
  
  /**
   * Get business day rate (fallback to previous business days)
   */
  async getBusinessDayRate(date, fromCurrency, toCurrency) {
    // Try the requested date first
    let result = await this.getFXRate(date, fromCurrency, toCurrency);
    if (result.success) {
      result.actualDate = date;
      return result;
    }
    
    // If failed, try previous business days (up to 7 days back)
    const dateObj = new Date(date);
    for (let daysBack = 1; daysBack <= 7; daysBack++) {
      const tryDate = new Date(dateObj);
      tryDate.setDate(dateObj.getDate() - daysBack);
      const tryDateStr = tryDate.toISOString().split('T')[0];
      
      console.log(`Trying previous business day: ${tryDateStr}`);
      result = await this.getFXRate(tryDateStr, fromCurrency, toCurrency);
      
      if (result.success) {
        result.actualDate = tryDateStr;
        console.log(`Found rate for business day ${tryDateStr} instead of ${date}`);
        return result;
      }
    }
    
    // If no rate found in the last 7 days, return the original error
    console.error(`No FX rate found for ${fromCurrency} -> ${toCurrency} within 7 days of ${date}`);
    return {
      rate: null,
      date: date,
      actualDate: date,
      success: false,
      error: 'No rate available for requested date or recent business days',
      cached: false
    };
  },
  
  /**
   * Clear cached rates
   */
  clearCache(fromCurrency = null, toCurrency = null, date = null) {
    if (date && fromCurrency && toCurrency) {
      // Clear specific rate
      const cacheKey = this.getCacheKey(date, fromCurrency, toCurrency);
      localStorage.removeItem(cacheKey);
      console.log(`Cleared cache for ${fromCurrency} -> ${toCurrency} on ${date}`);
    } else {
      // Clear all FX rate cache
      const keys = Object.keys(localStorage);
      const fxKeys = keys.filter(key => key.startsWith('fx_rate_'));
      fxKeys.forEach(key => localStorage.removeItem(key));
      console.log(`Cleared ${fxKeys.length} FX rate cache entries`);
    }
  },
  
  /**
   * Debounced version of getFXRate for rapid currency changes
   */
  debouncedGetFXRate: null,
  
  /**
   * Initialize debounced function
   */
  init() {
    // Create debounced version with 400ms delay as specified
    this.debouncedGetFXRate = this.debounce(this.getFXRate.bind(this), 400);
  },
  
  /**
   * Debounce utility function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
};

// Initialize the service
FX_RATE_SERVICE.init();

export default FX_RATE_SERVICE; 